import React, { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import {
  alpha,
  Box,
  Button,
  Pagination,
  Paper,
  IconButton,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton as MuiIconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';
import { Edit, CloseRounded } from '@mui/icons-material';
import DisplayQuillContent from './DisplayQuillContent';
import { FlowBreadcrumbs, FlowPageHeader, FlowStatGrid, SoftPanel, truncateText } from './FlowLayout';
import { TableRowsRounded } from '@mui/icons-material';

const TABLE_CONFIG = {
  aaryan_home_services: {
    listColumns: ['id', 'text_display_order', 'text_icon_key', 'text_card_title', 'text_card_description', 'text_is_active'],
    hiddenFields: ['text_section_title', 'text_section_subtitle'],
    summary: 'Edit only the service card data here.'
  },
  aaryan_home_why_choose: {
    listColumns: ['id', 'text_display_order', 'text_icon_key', 'text_stat_value', 'text_stat_label', 'text_is_active'],
    hiddenFields: ['text_section_title'],
    summary: 'Edit only the counter data here.'
  },
  aaryan_home_topbar: {
    listColumns: ['id', 'text_phone_display', 'text_phone_link', 'text_email_display', 'text_email_link'],
    hiddenFields: [],
    summary: 'This table controls the top contact strip shown in the homepage navbar.'
  }
};

const EditOnlyTablePage = () => {
  const { tableName, pageTitle, viewType } = useParams();
  const [tableData, setTableData] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [columns, setColumns] = useState([]);
  const [modalOpen, setModalOpen] = useState(false);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(false);
  const [previews, setPreviews] = useState({});
  const limit = 20;
  const baseApiUrl = 'https://aaryanholidays.in/backend/';
  const baseImagePath = 'https://aaryanholidays.in/express/uploads/';
  const tableConfig = TABLE_CONFIG[tableName] || { listColumns: null, hiddenFields: [], summary: '' };
  const isMobile = useMediaQuery('(max-width:900px)');

  const fetchData = useCallback(async () => {
    try {
      const response = await axios.get(`${baseApiUrl}table-data/${tableName}`, {
        params: { page: currentPage, search },
      });
      const rows = response?.data?.data || [];
      setTableData(rows);
      setTotalRecords(response?.data?.totalRecords || 0);

      if (rows.length > 0) {
        setColumns(Object.keys(rows[0]));
      }
    } catch (error) {
      console.error('Error fetching table data:', error);
    }
  }, [baseApiUrl, currentPage, search, tableName]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearchChange = (event) => {
    setSearch(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, value) => {
    setCurrentPage(value);
  };

  const handleOpenModal = (record) => {
    setFormData(record);
    setPreviews({});
    setModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, files, value } = e.target;

    if (files && files[0]) {
      const file = files[0];
      setFormData((prev) => ({ ...prev, [name]: file }));
      setPreviews((prev) => ({ ...prev, [name]: URL.createObjectURL(file) }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleQuillChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const formDataObj = new FormData();
    Object.keys(formData).forEach((key) => formDataObj.append(key, formData[key]));

    try {
      await axios.put(`${baseApiUrl}table-data/${tableName}/${formData.id}`, formDataObj);
      setModalOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving data:', error);
    } finally {
      setLoading(false);
    }
  };

  const renderField = (column, value) => {
    if (column.startsWith('image_')) {
      return <img src={`${baseImagePath}/${value}`} alt="content" width="100" />;
    }

    if (column.startsWith('video_')) {
      return (
        <video width="100" controls>
          <source src={`${baseImagePath}/${value}`} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
      );
    }

    if (column.startsWith('pdf_')) {
      return (
        <a href={`${baseImagePath}/${value}`} target="_blank" rel="noopener noreferrer">
          Download PDF
        </a>
      );
    }

    return <DisplayQuillContent content={value} />;
  };

  const formatLabel = (column) => {
    return column
      .replace(/^image_|^video_|^pdf_|^text_/, '')
      .replace(/_/g, ' ')
      .toUpperCase();
  };

  const summarizeValue = (column, value) => {
    if (column === 'id') {
      return value;
    }

    if (column.startsWith('image_')) {
      return <img src={`${baseImagePath}/${value}`} alt="preview" style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} />;
    }

    if (column.startsWith('video_')) {
      return (
        <video style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 16 }} controls>
          <source src={`${baseImagePath}/${value}`} type="video/mp4" />
        </video>
      );
    }

    if (column.startsWith('pdf_')) {
      return 'PDF file';
    }

    return truncateText(String(value || '').replace(/<[^>]*>/g, ''), 120);
  };

  const getVisibleColumns = () => {
    if (!columns.length) {
      return [];
    }

    if (!tableConfig.listColumns) {
      return columns;
    }

    return tableConfig.listColumns.filter((column) => columns.includes(column));
  };

  const visibleColumns = getVisibleColumns();

  const getModalColumns = () => {
    if (!columns.length) {
      return [];
    }

    const nonIdColumns = columns.filter((column) => column !== 'id');
    return nonIdColumns.filter((column) => !tableConfig.hiddenFields.includes(column));
  };

  const modalColumns = getModalColumns();

  const renderModalField = (column) => (
    <Box
      key={column}
      sx={{
        p: 2,
        borderRadius: 3,
        border: '1px solid rgba(148, 163, 184, 0.16)',
        backgroundColor: '#f8fafc'
      }}
    >
      <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.08em' }}>
        {formatLabel(column)}
      </Typography>
      <Box sx={{ mt: 1.25 }}>
        {column.startsWith('text_') && (
          <TextField
            name={column}
            value={formData[column] || ''}
            onChange={handleInputChange}
            fullWidth
            multiline
            minRows={column.includes('description') ? 3 : 1}
          />
        )}

        {column.startsWith('image_') && (
          <Stack spacing={1.5}>
            {previews[column] ? (
              <img src={previews[column]} alt="preview" style={{ width: '100%', maxWidth: 220, borderRadius: 16 }} />
            ) : (
              formData[column] && (
                <img src={`${baseImagePath}/${formData[column]}`} alt="preview" style={{ width: '100%', maxWidth: 220, borderRadius: 16 }} />
              )
            )}
            <input type="file" name={column} onChange={handleInputChange} />
          </Stack>
        )}

        {column.startsWith('video_') && (
          <Stack spacing={1.5}>
            {previews[column] ? (
              <video style={{ width: '100%', maxWidth: 260, borderRadius: 16 }} controls>
                <source src={previews[column]} type="video/mp4" />
              </video>
            ) : (
              formData[column] && (
                <video style={{ width: '100%', maxWidth: 260, borderRadius: 16 }} controls>
                  <source src={`${baseImagePath}/${formData[column]}`} type="video/mp4" />
                </video>
              )
            )}
            <input type="file" name={column} onChange={handleInputChange} />
          </Stack>
        )}

        {column.startsWith('pdf_') && (
          <Stack spacing={1.5}>
            {previews[column] ? (
              <a href={previews[column]} target="_blank" rel="noopener noreferrer">
                View PDF
              </a>
            ) : (
              formData[column] && (
                <a href={`${baseImagePath}/${formData[column]}`} target="_blank" rel="noopener noreferrer">
                  View PDF
                </a>
              )
            )}
            <input type="file" name={column} onChange={handleInputChange} />
          </Stack>
        )}

        {!(column.startsWith('image_') || column.startsWith('video_') || column.startsWith('pdf_') || column.startsWith('text_')) && (
          <Box
            sx={{
              '& .ql-toolbar': {
                borderTopLeftRadius: 12,
                borderTopRightRadius: 12,
                borderColor: alpha('#94a3b8', 0.35),
                backgroundColor: '#ffffff'
              },
              '& .ql-container': {
                minHeight: 180,
                borderBottomLeftRadius: 12,
                borderBottomRightRadius: 12,
                borderColor: alpha('#94a3b8', 0.35),
                backgroundColor: '#ffffff'
              },
              '& .ql-editor': {
                minHeight: 140,
                maxHeight: 260,
                overflowY: 'auto'
              }
            }}
          >
            <ReactQuill
              name={column}
              value={formData[column] || ''}
              onChange={(value) => handleQuillChange(column, value)}
              theme="snow"
            />
          </Box>
        )}
      </Box>
    </Box>
  );

  const renderLandscapeTable = () => (
    <TableContainer component={Paper}>
      <Table>
        <TableHead>
          <TableRow>
            {visibleColumns.map((column) => (
              <TableCell key={column}>{formatLabel(column)}</TableCell>
            ))}
            <TableCell>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {tableData.map((row, index) => (
            <TableRow key={index}>
              {visibleColumns.map((column) => (
                <TableCell key={`${column}-${index}`}>{renderField(column, row[column])}</TableCell>
              ))}
              <TableCell>
                <IconButton onClick={() => handleOpenModal(row)}>
                  <Edit />
                </IconButton>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  const renderPortraitTable = () => (
    <Stack spacing={2}>
      {tableData.map((row, index) => (
        <Box
          key={index}
          sx={{
            p: 2,
            borderRadius: 4,
            border: '1px solid rgba(148, 163, 184, 0.16)',
            backgroundColor: '#ffffff',
            boxShadow: '0 12px 26px rgba(15, 23, 42, 0.05)'
          }}
        >
          <Stack spacing={1.5}>
            <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
              <Typography variant="h6" sx={{ fontWeight: 800 }}>
                Record {row.id || index + 1}
              </Typography>
              <IconButton onClick={() => handleOpenModal(row)}>
                <Edit />
              </IconButton>
            </Stack>
            {visibleColumns.map((column) => (
              <Box key={`${column}-${index}`}>
                <Typography variant="overline" sx={{ color: '#64748b', letterSpacing: '0.08em' }}>
                  {formatLabel(column)}
                </Typography>
                <Box sx={{ mt: 0.75 }}>{summarizeValue(column, row[column])}</Box>
              </Box>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );

  return (
    <Box sx={{ p: 2 }}>
      <FlowBreadcrumbs items={[{ label: 'Homepage Editor' }, { label: pageTitle }]} />

      <FlowPageHeader
        title={`${pageTitle} Data`}
        subtitle={tableConfig.summary || 'Review and update homepage records through a cleaner editor surface.'}
        search={search}
        searchPlaceholder={`Search ${pageTitle.toLowerCase()} records`}
        onSearchChange={handleSearchChange}
      />

      <FlowStatGrid
        items={[
          {
            label: 'Records',
            value: totalRecords,
            icon: TableRowsRounded,
            tone: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)'
          }
        ]}
      />

      <SoftPanel>
        {viewType === 'potrait' || isMobile ? renderPortraitTable() : renderLandscapeTable()}
      </SoftPanel>

      <Box mt={2} display="flex" justifyContent="center">
        <Pagination
          count={Math.ceil(totalRecords / limit)}
          page={currentPage}
          onChange={handlePageChange}
          color="primary"
        />
      </Box>

      <Dialog
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        fullScreen={isMobile}
        fullWidth
        maxWidth="lg"
        scroll="paper"
        PaperProps={{
          sx: {
            display: 'flex',
            flexDirection: 'column',
            borderRadius: isMobile ? 0 : 5,
            overflow: 'hidden',
            minHeight: isMobile ? '100vh' : 'auto',
            maxHeight: isMobile ? '100vh' : '88vh'
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.5, fontWeight: 800 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>
              Edit Record
            </Typography>
            <MuiIconButton
              onClick={() => setModalOpen(false)}
              sx={{
                width: 38,
                height: 38,
                border: '1px solid rgba(148, 163, 184, 0.2)',
                backgroundColor: '#ffffff',
                '&:hover': {
                  backgroundColor: '#f8fafc'
                }
              }}
            >
              <CloseRounded fontSize="small" />
            </MuiIconButton>
          </Stack>
        </DialogTitle>
        <Divider />
        <form onSubmit={handleFormSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <DialogContent
            dividers={false}
            sx={{
              flex: 1,
              minHeight: 0,
              p: { xs: 2, md: 3 },
              overflowY: 'auto',
              scrollbarWidth: 'thin',
              scrollbarColor: 'rgba(59,130,246,0.45) transparent',
              '&::-webkit-scrollbar': {
                width: 10
              },
              '&::-webkit-scrollbar-track': {
                background: 'transparent'
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(59,130,246,0.32)',
                borderRadius: 999,
                border: '2px solid transparent',
                backgroundClip: 'padding-box'
              },
              '&::-webkit-scrollbar-thumb:hover': {
                backgroundColor: 'rgba(37,99,235,0.48)'
              }
            }}
          >
            <Stack spacing={2.25}>
              {modalColumns.map((column) => renderModalField(column))}
            </Stack>
          </DialogContent>
          <Divider />
          <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, justifyContent: 'space-between', backgroundColor: '#ffffff' }}>
            <Button
              onClick={() => setModalOpen(false)}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                borderRadius: 999,
                px: 2.5,
                color: '#334155'
              }}
            >
              Cancel
            </Button>
            {loading ? (
              <Box display="flex" justifyContent="center" alignItems="center" minWidth={120}>
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Button
                variant="contained"
                color="primary"
                type="submit"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: 999,
                  px: 3.2,
                  py: 1.15,
                  background: 'linear-gradient(135deg, #0ea5e9 0%, #2563eb 100%)',
                  boxShadow: '0 18px 34px rgba(37, 99, 235, 0.22)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #0284c7 0%, #1d4ed8 100%)'
                  }
                }}
              >
                Save Changes
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  );
};

export default EditOnlyTablePage;
